import { skipToken, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

export type JobMode = 'poll' | 'manual' | 'sse';

export interface PollPolicy {
  /** First interval after the job is observed running. */
  initialMs: number;
  /** Upper bound for the backoff. */
  maxMs: number;
  /** Multiplier applied after each poll. */
  factor: number;
  /** Stop polling after this long and hand over to manual "Check status". */
  timeoutMs: number;
  /** Minimum gap between manual checks so the button cannot be hammered. */
  cooldownMs: number;
}

/** Defaults agreed for v1: 2s→10s backoff, stop at 90s, 10s cooldown on manual checks. */
export const DEFAULT_POLL_POLICY: PollPolicy = {
  initialMs: 2_000,
  maxMs: 10_000,
  factor: 1.6,
  timeoutMs: 90_000,
  cooldownMs: 10_000,
};

export interface JobTrackerOptions<TJob> {
  jobId: string | null | undefined;
  fetchJob: (jobId: string) => Promise<TJob>;
  isTerminal: (job: TJob) => boolean;
  /** poll (default): bounded auto-polling. manual: only on checkNow(). sse: reserved, behaves as manual for now. */
  mode?: JobMode;
  policy?: Partial<PollPolicy>;
  onTerminal?: (job: TJob) => void;
  /**
   * Epoch ms the timeout counts from, e.g. when the job was created on this screen. Defaults to
   * when the tracker first saw `jobId`.
   */
  startedAt?: number | undefined;
  /** Cache key prefix; the job id is appended. Defaults to `['jobs']`. */
  queryKey?: readonly unknown[];
}

export type JobTrackerStatus = 'idle' | 'polling' | 'stalled' | 'done' | 'error';

export interface JobTrackerState<TJob> {
  job: TJob | undefined;
  status: JobTrackerStatus;
  error: unknown;
  /** Manually re-check; ignored while cooling down. */
  checkNow: () => void;
  canCheck: boolean;
  cooldownRemainingMs: number;
}

const DEFAULT_PREFIX: readonly unknown[] = ['jobs'];

export function jobQueryKey(jobId: string, prefix: readonly unknown[] = DEFAULT_PREFIX) {
  return [...prefix, jobId] as const;
}

/**
 * Delay before the next poll, or false to stop: no answer yet, a terminal job, or past the
 * timeout. `polls` counts the answers received so far, so the first wait is `initialMs`.
 * Pure on purpose: TanStack Query evaluates `refetchInterval` while rendering, so any state
 * update in there re-renders forever ("Maximum update depth exceeded").
 */
export function nextPollDelay<TJob>(
  job: TJob | undefined,
  polls: number,
  elapsedMs: number,
  isTerminal: (job: TJob) => boolean,
  policy: PollPolicy = DEFAULT_POLL_POLICY,
): number | false {
  if (job === undefined || isTerminal(job) || elapsedMs >= policy.timeoutMs) return false;
  const step = Math.max(0, polls - 1);
  return Math.min(policy.initialMs * policy.factor ** step, policy.maxMs);
}

/**
 * Tracks a background job with the agreed policy. Every value is overridable per job type.
 * Polling never runs forever: after `timeoutMs` the tracker goes "stalled" and the UI offers a
 * manual check with a cooldown. The start time lives in a ref written from an effect, and one
 * timer re-renders when the timeout passes; nothing sets state from `refetchInterval`.
 */
export function useJobTracker<TJob>({
  jobId,
  fetchJob,
  isTerminal,
  mode = 'poll',
  policy: policyOverride,
  onTerminal,
  startedAt: startedAtOption,
  queryKey: prefix = DEFAULT_PREFIX,
}: JobTrackerOptions<TJob>): JobTrackerState<TJob> {
  const policy = { ...DEFAULT_POLL_POLICY, ...policyOverride };
  const { timeoutMs, cooldownMs } = policy;
  const start = useRef<{ jobId: string; at: number } | null>(null);
  /** The job id whose timeout has passed. */
  const [timedOut, setTimedOut] = useState<string | null>(null);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [now, setNow] = useState(0);
  const terminalNotified = useRef<string | null>(null);

  const query = useQuery({
    queryKey: jobId ? jobQueryKey(jobId, prefix) : [...prefix, 'none'],
    queryFn: jobId ? () => fetchJob(jobId) : skipToken,
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchInterval: (q) => {
      if (mode !== 'poll' || !jobId) return false;
      const startedAt = start.current?.jobId === jobId ? start.current.at : Date.now();
      return nextPollDelay(
        q.state.data,
        q.state.dataUpdateCount,
        Date.now() - startedAt,
        isTerminal,
        policy,
      );
    },
  });

  const job = query.data;
  const terminalJob = job !== undefined && isTerminal(job) ? job : undefined;
  const done = terminalJob !== undefined;

  useEffect(() => {
    if (!jobId) return;
    const known = start.current?.jobId === jobId ? start.current.at : undefined;
    const startedAt = startedAtOption ?? known ?? Date.now();
    start.current = { jobId, at: startedAt };
    if (done || mode !== 'poll') return;
    const id = window.setTimeout(
      () => setTimedOut(jobId),
      Math.max(0, startedAt + timeoutMs - Date.now()),
    );
    return () => window.clearTimeout(id);
  }, [jobId, startedAtOption, timeoutMs, done, mode]);

  useEffect(() => {
    if (terminalJob === undefined || !jobId || terminalNotified.current === jobId) return;
    terminalNotified.current = jobId;
    onTerminal?.(terminalJob);
  }, [terminalJob, jobId, onTerminal]);

  // Tick while cooling down so the UI can show a countdown.
  useEffect(() => {
    if (cooldownUntil === 0) return;
    const id = window.setInterval(() => {
      const at = Date.now();
      setNow(at);
      if (at >= cooldownUntil) window.clearInterval(id);
    }, 250);
    return () => window.clearInterval(id);
  }, [cooldownUntil]);

  const stalled = jobId != null && timedOut === jobId;
  const cooldownRemainingMs = Math.max(0, cooldownUntil - now);
  const canCheck = Boolean(jobId) && !done && cooldownRemainingMs === 0 && !query.isFetching;
  const { refetch } = query;

  const checkNow = useCallback(() => {
    if (!jobId || !canCheck) return;
    const at = Date.now();
    setCooldownUntil(at + cooldownMs);
    setNow(at);
    void refetch();
  }, [jobId, canCheck, cooldownMs, refetch]);

  let status: JobTrackerStatus = 'idle';
  if (query.isError) status = 'error';
  else if (done) status = 'done';
  else if (stalled || mode !== 'poll') status = jobId ? 'stalled' : 'idle';
  else if (jobId) status = 'polling';

  return { job, status, error: query.error, checkNow, canCheck, cooldownRemainingMs };
}
